import { useCallback, useEffect, useRef } from 'react';

/**
 * Kjøringen og skjermen i rommet er to vinduer i samme nettleser, og skjermen følger kjøringen
 * gjennom en BroadcastChannel (DD-47 presisert 2026-10-29). Kanalen er ikke lagring — den ligger
 * utenfor den lukkede `localStorage`-lista (M2.6/M2.7) — og den går aldri via tjeneren, så skjermen
 * lever videre uten nett (W8b).
 *
 * Meldingene bærer bare HVILKEN hypotese som vises og at noe er registrert — aldri utfallet, tallene
 * eller notatet. Skjermen henter det den viser selv, fra den lukkede `MeetingScreen` (P1).
 */
export type MoeteMelding =
  /** Vis denne hypotesen. Kjøringen sender når den bytter rad; skjermen når noen trykker ← eller →. */
  | { type: 'vis'; hypoteseId: string }
  /** Et utfall er registrert: skjermen henter pillen på nytt. */
  | { type: 'utfall' }
  /** «Avslutt møtet»: skjermen lukker seg. */
  | { type: 'lukk' }
  /** Kjøringen spør om skjermen er åpen. */
  | { type: 'ping' }
  /** Skjermen svarer, og melder seg når den åpnes. */
  | { type: 'her' }
  /** Skjermen er lukket. */
  | { type: 'borte' };

export const kanalNavn = (moeteId: string): string => `innspel-moete-${moeteId}`;

/** Skjermen i rommet er en egen rute i panelet uten skall (DD-47). */
export const skjermSti = (moeteId: string): string => `/h/moter/${moeteId}/skjerm`;

/**
 * Åpner skjermen i rommet i et eget vindu med fast navn, så et nytt trykk gjenbruker det samme
 * vinduet i stedet for å åpne to. Samme opprinnelse: CSP-en (`default-src 'none'`) rammer det ikke.
 */
export function aapneSkjermen(moeteId: string): void {
  window.open(skjermSti(moeteId), `innspel-skjermen-i-rommet-${moeteId}`, 'popup');
}

/**
 * Kanalen for ett møte. `onMelding` kalles for hver melding fra det andre vinduet; returverdien
 * sender. Uten BroadcastChannel (eldre nettleser) sender den ingenting og hører ingenting — kjøringen
 * virker da som før, og skjermen står på den hypotesen den ble åpnet med.
 */
export function useMoeteKanal(moeteId: string, onMelding: (m: MoeteMelding) => void): (m: MoeteMelding) => void {
  const kanal = useRef<BroadcastChannel | null>(null);
  const lytter = useRef(onMelding);
  lytter.current = onMelding;

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const k = new BroadcastChannel(kanalNavn(moeteId));
    k.onmessage = (e: MessageEvent<MoeteMelding>) => lytter.current(e.data);
    kanal.current = k;
    return () => {
      k.close();
      kanal.current = null;
    };
  }, [moeteId]);

  return useCallback((m: MoeteMelding) => {
    kanal.current?.postMessage(m);
  }, []);
}
